
import { useState, useEffect, useCallback } from "react";
import { getTodaysPuzzle } from "@/lib/game-utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useExternalPuzzleSource } from "./useExternalPuzzleSource";
import type { LogicRule } from "@/lib/game-utils";
import { ensureTodayGameExists, finalizeYesterdayGameIfNeeded, checkPuzzleCompletion } from "@/lib/database/daily-game-service";
import { getTodayString } from "@/utils/dateUtils";

export function usePuzzleData(email: string | null, externalSpreadsheetUrl?: string) {
  const [todaysPuzzle, setTodaysPuzzle] = useState<any>(null);
  const [existingAttempt, setExistingAttempt] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUsingExternalData, setIsUsingExternalData] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const { toast } = useToast();
  const spreadsheetUrl = externalSpreadsheetUrl || localStorage.getItem("external_puzzle_url") || "";
  const { externalPuzzle, isLoading: isExternalLoading, error } = useExternalPuzzleSource(spreadsheetUrl);

  useEffect(() => {
    const fetchPuzzleData = async () => {
      try {
        setIsLoading(true);
        setLoadingError(null);
        setExistingAttempt(null);
        setTodaysPuzzle(null);
        setIsUsingExternalData(!!spreadsheetUrl);

        let data = externalPuzzle;

        if (data) {
          // Try database sync with more robust error handling
          if (email) {
            try {
              await finalizeYesterdayGameIfNeeded();
              const gameId = await ensureTodayGameExists(data);
              
              if (!gameId) {
                console.warn("Game sync did not return a game ID - will continue with local puzzle data");
              }
              
              // Mark puzzle as completed if user has already done it
              const today = getTodayString();
              const hasCompleted = await checkPuzzleCompletion(email, today);
              if (hasCompleted) {
                console.log("User has already completed today's puzzle");
              }
            } catch (dbError) {
              console.error("Database sync error:", dbError);
              // Don't set error - continue with local data
            }
          }
          
          setTodaysPuzzle(data);
        } else {
          setLoadingError('No puzzle data found');
        }
      } catch (err) {
        console.error('Puzzle data fetch failed:', err);
        setLoadingError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsLoading(false);
      }
    };

    // Add a small timeout to ensure smooth transition
    const timer = setTimeout(fetchPuzzleData, 500);
    return () => clearTimeout(timer);
  }, [spreadsheetUrl, email, externalPuzzle]);

  const generateNewPuzzle = useCallback(() => {
    // Reset states to trigger re-fetch
    setIsLoading(true);
    setTodaysPuzzle(null);
  }, []);

  return {
    existingAttempt,
    todaysPuzzle,
    isLoading,
    generateNewPuzzle,
    error: loadingError || error,
    isUsingExternalData
  };
}
